import paths from '../../constants/paths';
import {
  ADMIN_PANEL_AGENCIES_ROUTES,
  ADMIN_PANEL_AGENCY_DOCTORS_ROUTES,
  ADMIN_PANEL_CLINICS_ROUTES,
  ADMIN_PANEL_USERS_ROUTES,
  ADMIN_PANEL_PATIENTS_ROUTES,
  PATIENT_ACCEPTANCE_ROUTES,
  CCM_ROUTES,
  RPM_ROUTES,
  RPM_ADD_ROUTES,
  RPM_PRACTICE_REPORT_ROUTES,
} from './routes';
import {
  ADMINISTRATOR_ROLE_NAME,
  ADMIN_USER_TYPE,
  PRACTICE_USER_TYPE,
  PATIENT_ACCEPTANCE_ROLE,
  HHA_USER_TYPE,
  PATIENT_USER_TYPE,
  CCM_PREFIX,
  PLATFORM_MODE_CCM,
  PLATFORM_MODE_RPM,
  PLATFORM_MODE_LOCALSTORAGE_KEY,
  PLATFORM_MODE_PATIENT_ACCEPTANCE,
  CRITICAL_ALERTING,
  AT_RISK_ALERTING,
  CLINICAL_ROLE_NAME,
} from './constants';

export const hasClinicalRole = (roleNames) =>
  roleNames &&
  roleNames.some((roleName) => roleName.includes(CLINICAL_ROLE_NAME));

const hasCCMRole = (roleNames) =>
  roleNames && roleNames.some((roleName) => roleName.includes(CCM_PREFIX));

export const hasPatientAcceptanceRole = (roleNames) =>
  roleNames &&
  roleNames.some((roleName) => roleName.includes(PATIENT_ACCEPTANCE_ROLE));

const hasRPMRole = (roleNames) =>
  roleNames &&
  roleNames.some(
    (roleName) =>
      !roleName.includes(CCM_PREFIX) &&
      !roleName.includes(ADMINISTRATOR_ROLE_NAME) &&
      !roleName.includes(CRITICAL_ALERTING) &&
      !roleName.includes(AT_RISK_ALERTING),
  );

export const isCriticalOrAtRiskAlertingRole = (roleNames) =>
  roleNames &&
  roleNames.every(
    (roleName) =>
      roleName === CRITICAL_ALERTING || roleName === AT_RISK_ALERTING,
  );

export const isCriticalRole = (roleNames) =>
  roleNames && roleNames.some((roleName) => roleName === CRITICAL_ALERTING);

export const isAtRiskRole = (roleNames) =>
  roleNames && roleNames.some((roleName) => roleName === AT_RISK_ALERTING);

export const hasAdminPanelAccess = (roleNames) =>
  roleNames.includes(ADMINISTRATOR_ROLE_NAME);

export const hasAllAccess = (user) => user.userType === ADMIN_USER_TYPE;

export const isHhaRole = (userType) => userType === HHA_USER_TYPE;

export const getUserRoutes = (user) => {
  const { userType, roleNames, platformMode } = user;
  const routes = [];

  if (user.userType === ADMIN_USER_TYPE) {
    routes.push(...ADMIN_PANEL_PATIENTS_ROUTES);
    routes.push(...ADMIN_PANEL_CLINICS_ROUTES);
    routes.push(...ADMIN_PANEL_USERS_ROUTES);
    routes.push(...ADMIN_PANEL_AGENCIES_ROUTES);
    routes.push(...ADMIN_PANEL_AGENCY_DOCTORS_ROUTES);
    routes.push(...RPM_PRACTICE_REPORT_ROUTES);
    routes.push(...RPM_ROUTES);
    routes.push(...RPM_ADD_ROUTES);
    routes.push(...CCM_ROUTES);

    const adminRoutes = [...new Set(routes)];

    return adminRoutes;
  }

  if (hasPatientAcceptanceRole(roleNames)) {
    routes.push(...PATIENT_ACCEPTANCE_ROUTES);
    return routes;
  }
  if (hasAdminPanelAccess(roleNames)) {
    routes.push(...ADMIN_PANEL_PATIENTS_ROUTES);
    routes.push(...ADMIN_PANEL_CLINICS_ROUTES);
    routes.push(...ADMIN_PANEL_USERS_ROUTES);
  }

  if (userType === PRACTICE_USER_TYPE) {
    if (platformMode === PLATFORM_MODE_RPM) routes.push(...RPM_ROUTES);
    else if (platformMode === PLATFORM_MODE_CCM) routes.push(...CCM_ROUTES);
  } else if (userType === HHA_USER_TYPE) {
    if (hasCCMRole(roleNames) && platformMode === PLATFORM_MODE_CCM) {
      routes.push(...CCM_ROUTES);
    }

    if (hasRPMRole(roleNames) && platformMode === PLATFORM_MODE_RPM) {
      routes.push(...RPM_ROUTES);
      routes.push(...RPM_ADD_ROUTES);
    }

    if (hasAdminPanelAccess(roleNames)) {
      routes.push(...ADMIN_PANEL_AGENCIES_ROUTES);
      routes.push(...ADMIN_PANEL_AGENCY_DOCTORS_ROUTES);
      routes.push(...RPM_PRACTICE_REPORT_ROUTES);
    }
  } else if (userType === PATIENT_USER_TYPE) {
    routes.push(paths.profile);
    routes.push(`/patient/${user?.guid}`);
  }
  return routes;
};

export const getUserPlatformMode = (user) => {
  const { userType, roleNames } = user;
  if (hasPatientAcceptanceRole(roleNames)) {
    return PLATFORM_MODE_PATIENT_ACCEPTANCE;
  }
  if (userType === PRACTICE_USER_TYPE) {
    const userPlatformMode = localStorage.getItem(
      PLATFORM_MODE_LOCALSTORAGE_KEY,
    );
    if (userPlatformMode) return userPlatformMode;

    localStorage.setItem(PLATFORM_MODE_LOCALSTORAGE_KEY, PLATFORM_MODE_RPM);
    return PLATFORM_MODE_RPM;
  }
  if (userType === HHA_USER_TYPE) {
    if (hasCCMRole(roleNames) && !hasRPMRole(roleNames))
      return PLATFORM_MODE_CCM;

    const userPlatformMode = localStorage.getItem(
      PLATFORM_MODE_LOCALSTORAGE_KEY,
    );
    if (userPlatformMode) return userPlatformMode;

    localStorage.setItem(PLATFORM_MODE_LOCALSTORAGE_KEY, PLATFORM_MODE_RPM);
    return PLATFORM_MODE_RPM;
  }
  return PLATFORM_MODE_RPM;
};

export const toggleUserPlatformMode = (user) => {
  const { platformMode } = user;

  let newMode = PLATFORM_MODE_RPM;
  if (platformMode === PLATFORM_MODE_RPM) {
    newMode = PLATFORM_MODE_CCM;
  }
  localStorage.setItem(PLATFORM_MODE_LOCALSTORAGE_KEY, newMode);

  return newMode;
};
